import {useCallback, useEffect, useState} from 'react';
import {Route, Routes, useLocation} from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';

import Onboarding from './components/onboarding/Onboarding';
//import Explore from './components/explore/Explore';
import Guide from './components/guide/Guide';
import Path from './components/path/Path';
import Bookmarks from './components/bookmarks/Bookmarks';
import Profile from './components/profile/Profile';
import Referrals from './components/referrals/Referrals';
import Feedback from './components/feedback/Feedback';
import Phone from './components/phone/Phone';
import DeleteAccountConfirmation from './components/account/DeleteAccountConfirmation';

import { deleteLocal, getActions, getLocal, resetProfile, sendAction, setLocal, syncProfile, updateProfileAsTester } from './utils/api';

import './App.css';

function App() {
  const { error: errorAuth, getAccessTokenSilently, isAuthenticated, isLoading: isLoadingAuth, loginWithRedirect, logout, user } = useAuth0();
  const location = useLocation();
  
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSignedUp, setIsSignedUp] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showPhone, setShowPhone] = useState(false);

  const confirmError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback(e => {
    setError(e.message);
  }, []);

  const handleFeedback = useCallback(show => {
    setShowFeedbackForm(show);
  }, []);

  const handlePhone = useCallback(e => {
    setShowPhone(prevShow => !prevShow);
  }, []);

  const confirmConnection = useCallback(connection => {
    setLocal('connection', connection);

    if (connection === 'sms') {
      setShowPhone(true);
    } else {
      window.location = '/';
    }
  }, []);

  const confirmTos = useCallback(async isAccepted => {
    try {
      if (isAccepted) {
        const referralCode = getLocal('referralCode');
        const response = await sendAction('signup', null, referralCode ? { referralCode } : null);

        if (response !== null) {
          setIsSignedUp(true);
          setLocal('referralCode', false);
        }
      } else {
        logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        });

        await resetProfile();
      }
    } catch (e) {
      handleError(e);
    }
  }, [handleError, logout]);

  const checkProfile = useCallback(async (email, phone) => {
    let response;

    try {
      response = await syncProfile(email, phone);

      if (response) {
        return response;
      }

      return null;
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  useEffect(() => {
    if (location.hash) {
      const hashParts = location.hash.substring(1).split('&');
      
      if (hashParts.length > 0) {
        hashParts.forEach(hashPart => {
          const valueParts = hashPart.split('=');

          if (valueParts.length === 2) {
            if (valueParts[0] === 'id_token' && valueParts[1]) {
              setLocal('login', true);
              setLocal('token', valueParts[1]);
            }
            
            window.location = '/';
          }
        });
      }
    }
  });

  useEffect(() => {
    const _connection = getLocal('connection');
    const _token = getLocal('token');

    const fetchAccessToken = async () => {
      let token;

      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        });

        await resetProfile();
        return;
      }

      setLocal('token', token);

      if (user) {
        let email;
        let phone;
        
        try {
          if (_connection === 'sms') {
            phone = user.name;
          } else {
            email = user.email;
          }

          const response = await checkProfile(email, phone);

          if (response?.status === 201) {
            await loginWithRedirect();
            return;
          }

          if (getLocal('login')) {
            if (await sendAction('login') !== null) {
              deleteLocal('login');
            }
          }

          const isTester = getLocal('isTester');

          if (isTester === '0' || isTester === '1') {
            if (await updateProfileAsTester(isTester === '1') !== null) {
              deleteLocal('isTester');
            }
          }

          const actions = await getActions('signup');

          if (actions?.length) {
            setIsSignedUp(true);
            setLocal('referralCode', false);
          }
  
          setIsLoading(false);
        } catch (e) {
          handleError(e);
        }
      } else {
        
      }

      return token;
    };

    if (!isLoadingAuth) {
      if (errorAuth) {
        handleError(errorAuth);
      } else if (!isAuthenticated && !_token) {
        if (_connection === 'email') {
          loginWithRedirect();
        } else {
          setIsLoading(false);
        }
      } else {
        const locationParts = location.pathname.split('/');

        if (locationParts.length === 3 && locationParts[1] === 'referral' && locationParts[2]) {
          window.location = '/';
          return;
        }

        if (locationParts.length === 3 && locationParts[1] === 'tester' && (locationParts[2] === '0' || locationParts[2] === '1')) {
          setLocal('isTester', locationParts[2]);

          window.location = '/';
          return;
        }

        try {
          fetchAccessToken();
        } catch (e) {
          handleError(e);
        }
      }
    }
  }, [checkProfile, errorAuth, getAccessTokenSilently, handleError, isAuthenticated, isLoadingAuth, location, loginWithRedirect, logout]);

  return (
    <>
      {isAuthenticated && !isLoading && !isLoadingAuth && isSignedUp ? (
        <div id="app">
          <Routes>
            <Route path="/" element={<Onboarding handleError={handleError} handleFeedback={handleFeedback} />} />
            {/*<Route path="/explore" element={<Explore handleError={handleError} handleFeedback={handleFeedback} />} />*/}
            <Route path="/guide" element={<Guide handleError={handleError} handleFeedback={handleFeedback} />} />
            <Route path="/guide/path/:tileId" element={<Guide handleError={handleError} handleFeedback={handleFeedback} />} />
            <Route path="/guide/star/:starId" element={<Guide handleError={handleError} handleFeedback={handleFeedback} />} />
            <Route path="/path" element={<Path handleError={handleError} handleFeedback={handleFeedback} />} />
            <Route path="/bookmarks" element={<Bookmarks handleError={handleError} handleFeedback={handleFeedback} />} />
            <Route path="/profile" element={<Profile handleError={handleError} handleFeedback={handleFeedback} />} />
            <Route path="/referrals" element={<Referrals handleError={handleError} handleFeedback={handleFeedback} />} />
            <Route path="/delete-account" element={<DeleteAccountConfirmation handleError={handleError} />} />
          </Routes>
        </div>
      ) : null}
      {isAuthenticated && !isLoading && !isLoadingAuth && !isSignedUp ? (
        <div id="tos-container" className="popup-overlay">
          <div className="popup">
            <p>I am 18 years of age or older and agree to the <a href="https://www.campusevolve.ai/copy-of-terms-2" target="_blank" rel="noreferrer">Terms of Service</a>.</p>
            <div className="actions">
              <div className="action action-primary" onClick={() => confirmTos(true)}>Accept</div>
              <div className="action action-secondary" onClick={() => confirmTos(false)}>Cancel</div>
            </div>
          </div>
        </div>
      ) : null}
      {!isAuthenticated && !isLoading && !isLoadingAuth && !showPhone ? (
        <div id="connection-container" className="popup-overlay">
          <div className="popup">
            <p>How would you like to login?</p>
            <div className="actions">
              <div className="action action-primary" onClick={() => confirmConnection('email')}>By Email</div>
              <div className="action action-primary" onClick={() => confirmConnection('sms')}>By Phone</div>
            </div>
          </div>
        </div>
      ) : null}
      {!isAuthenticated && !isLoading && !isLoadingAuth && showPhone ? (
        <Phone handleError={handleError} handlePhone={handlePhone} />
      ) : null}
      {showFeedbackForm ? (
        <Feedback handleError={handleError} handleFeedback={handleFeedback} />
      ) : null}
      {error ? (
        <div id="error-container" className="popup-overlay" onClick={() => confirmError()}>
          <div id="error" className="popup">
            <span>Sorry, please try again</span>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default App;
